import type { ReactNode } from "react";
import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";
import { shiftLabels } from "./line-options";

const value = (input: unknown) =>
  input === null || input === undefined || input === "" ? "—" : String(input);
const dateTime = (input: string | null) =>
  input
    ? new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(input))
    : "—";
const checklistLabels = {
  TANK_AND_PIPING: "Tank ve Boru Tesisat",
  MOVING_PARTS: "Hareketli Aksam",
  GUARDS_SENSORS_SWITCHES: "Muhafaza / Sensör / Switch",
  MOLD: "Kalıp Adeti",
  REEL_PACKAGING_LABEL_BOX: "Bobin / Ambalaj / Etiket / Koli",
} as const;
const measurementLabels = {
  THEORETICAL_LINE_SPEED: "Teorik Makina Hat Hızı / Adet",
  ACTUAL_LINE_SPEED: "Makina Hat Hızı / Adet",
  MACHINE_TEMPERATURE: "Makina Isı Derecesi",
  OVEN_TEMPERATURE: "Makine Isı Dereceleri / Fırın",
  MOLD_TEMPERATURE: "Makine Isı Dereceleri / Kalıp",
  TUNNEL_TEMPERATURE: "Makine Isı Dereceleri / Tünel",
  UNIT_WEIGHT: "Ürün Ağırlığı",
  PRODUCT_MOISTURE: "Ürün Nem",
  AMBIENT_TEMPERATURE: "Ortam Sıcaklık",
  AMBIENT_HUMIDITY: "Ortam Nem %",
} as const;

export function ShiftFormReadOnly({
  payload,
}: {
  payload: ShiftProductionFormPayload;
}) {
  return (
    <div className="shift-readonly">
      <ReadSection title="VARDİYA ÜRETİM FORMU">
        <ReadGrid
          entries={[
            ["Doküman No", payload.documentNo],
            ["Yayın Tarihi", payload.publicationDate],
            ["Revizyon Tarihi", payload.revisionDate],
            ["Revizyon No", payload.revisionNo],
            ["Operatör Adı", payload.operatorName],
            ["Bölüm Adı", payload.departmentName],
            ["Makina Adı", payload.lineName],
            ["Tarih", payload.productionDate],
            ["Vardiya", payload.shift ? shiftLabels[payload.shift] : null],
          ]}
        />
      </ReadSection>
      <ReadSection title="Hat Kontrolü">
        <ReadGrid
          entries={payload.checklist.map((item) => [
            checklistLabels[item.key],
            item.state === "SUITABLE"
              ? "Uygun"
              : item.state === "UNSUITABLE"
                ? "Değil"
                : item.state === "NOT_CHECKED"
                  ? "Kontrol edilmedi"
                  : item.state === "NOT_APPLICABLE"
                    ? "Uygulanmaz"
                    : null,
          ])}
        />
        <ReadGrid
          entries={payload.checklist
            .filter((item) => item.state === "UNSUITABLE" && item.explanation.trim())
            .map((item) => [
              `${checklistLabels[item.key]} / Uygunsuzluk Açıklaması`,
              item.explanation,
            ])}
        />
        {payload.checklistExplanation.trim() ? (
          <p><strong>Uygunsuzluk Açıklaması:</strong> {payload.checklistExplanation}</p>
        ) : null}
      </ReadSection>
      {payload.products.map((product, index) => (
        <div key={product.id ?? index}>
          <ReadSection title="Çalışma Zaman">
            <TimeGrid
              start={product.machineStartedAt}
              delay={
                product.delayReason ?? product.machineStartNotOccurredReason
              }
              first={product.firstProductAt}
              firstDelay={product.firstProductNotOccurredReason}
            />
          </ReadSection>
          <ReadSection title="Makina / Ürün / Ortam Parametreleri">
            <ReadGrid
              entries={measurementEntries(product)}
            />
          </ReadSection>
          <ReadSection title="Hat Hızındaki Değişiklikler">
            <ReadTable
              columns={[
                "Makina Hat Hızı / Adet",
                "Sebebi",
                "Bilgi Verilen Yetkili",
              ]}
              rows={product.speedChanges.map((item) => [
                item.newSpeed === null
                  ? null
                  : `${item.newSpeed} ${item.unit ?? ""}`,
                item.reason,
                item.informedPerson,
              ])}
            />
          </ReadSection>
          <ReadSection title="Hat Duruş">
            <ReadTable
              columns={[
                "Hat Duruş Başlangıç Saati",
                "Hat Duruş Bitiş Saati",
                "Sebebi",
                "Teknik Bakım Talep Fiş No",
                "Sorumlu Bölüm",
              ]}
              rows={product.downtimes.map((item) => [
                dateTime(item.startedAt),
                dateTime(item.endedAt),
                item.reason,
                item.maintenanceTicketNo,
                item.responsibleDepartment,
              ])}
            />
          </ReadSection>
          <ReadSection title="Ürün Değişim">
            <ReadTable
              columns={["Sebebi", "Adam Sayısı", "Sorumlu Bölüm"]}
              rows={product.productChanges.map((item) => [
                item.reason,
                item.participantCount,
                item.responsibleDepartment,
              ])}
            />
            <TimeGrid
              start={product.postChangeMachineStartedAt}
              delay={product.postChangeDelayReason}
              first={product.postChangeFirstProductAt}
              firstDelay={product.postChangeFirstProductDelayReason}
            />
          </ReadSection>
          <ReadSection title="Ürün Fire-Iskarta Oranları">
            <ReadTable
              columns={[
                "Iskarta",
                "Hayvan Yemi (GSF)",
                "Bobin Fire",
                "Kutu Fire",
                "Koli Fire",
                "Etiket Fire",
              ]}
              rows={[
                [
                  product.quantities.scrapKg,
                  product.quantities.animalFeedKg,
                  product.quantities.reelWasteQuantity,
                  product.quantities.boxWasteCount,
                  product.quantities.cartonWasteCount,
                  product.quantities.labelWasteCount,
                ],
              ]}
            />
            <ReadGrid
              entries={[
                ["Sebebi", product.quantities.wasteReason],
                [
                  "Sorumlu Bölüm",
                  product.quantities.wasteResponsibleDepartment,
                ],
              ]}
            />
          </ReadSection>
          <OutputQuantities quantities={product.quantities} />
        </div>
      ))}
      <ReadSection title="Açıklama">
        <p>
          {value(
            payload.generalExplanation ||
              payload.products
                .map((product) => product.explanation)
                .filter(Boolean)
                .join("; "),
          )}
        </p>
      </ReadSection>
    </div>
  );
}

function measurementEntries(product: ShiftProductionFormPayload["products"][number]) {
  const theoretical = product.measurements.find(
    (item) => item.key === "THEORETICAL_LINE_SPEED",
  );
  const hasHistoricalTheoretical = Boolean(
    theoretical && (theoretical.state || theoretical.value !== null),
  );
  const optionalTemperatures = new Set(["OVEN_TEMPERATURE", "MOLD_TEMPERATURE", "TUNNEL_TEMPERATURE"]);
  return [
    ...(hasHistoricalTheoretical && theoretical
      ? [[measurementLabels[theoretical.key], measurementValue(theoretical)] as [string, unknown]]
      : []),
    ...(hasHistoricalTheoretical
      ? [["Teorik Hızdan Sapma Sebebi", product.speedDeviationReason] as [string, unknown]]
      : []),
    ...product.measurements
      .filter((item) =>
        item.key !== "THEORETICAL_LINE_SPEED" &&
        (!optionalTemperatures.has(item.key) || item.state || item.value !== null),
      )
      .map(
        (item) =>
          [measurementLabels[item.key], measurementValue(item)] as [string, unknown],
      ),
  ];
}

function OutputQuantities({
  quantities,
}: {
  quantities: ShiftProductionFormPayload["products"][number]["quantities"];
}) {
  const hasHistoricalTargets = [
    quantities.targetCarton,
    quantities.targetCount,
    quantities.targetKg,
  ].some((value) => value !== null);
  const columns = hasHistoricalTargets
    ? [
        "Koli / Çıkması Gereken",
        "Koli / Vardiya Sonu Çıkan",
        "Adet / Çıkması Gereken",
        "Adet / Vardiya Sonu Çıkan",
        "Kilogram / Çıkması Gereken",
        "Kilogram / Vardiya Sonu Çıkan",
      ]
    : ["Koli / Vardiya Sonu Çıkan", "Adet / Vardiya Sonu Çıkan", "Kilogram / Vardiya Sonu Çıkan"];
  const row = hasHistoricalTargets
    ? [
        quantities.targetCarton,
        quantities.actualCarton,
        quantities.targetCount,
        quantities.actualCount,
        quantities.targetKg,
        quantities.actualKg,
      ]
    : [quantities.actualCarton, quantities.actualCount, quantities.actualKg];
  return (
    <ReadSection title="Çıkan Ürün Miktarı">
      <ReadTable columns={columns} rows={[row]} />
    </ReadSection>
  );
}
function measurementValue(
  item: ShiftProductionFormPayload["products"][number]["measurements"][number],
) {
  return item.value === null ? null : `${item.value} ${item.unit ?? ""}`;
}
function TimeGrid({
  start,
  delay,
  first,
  firstDelay,
}: {
  start: string | null;
  delay: string | null;
  first: string | null;
  firstDelay: string | null;
}) {
  return (
    <ReadGrid
      entries={[
        ["Makina Start Zamanı", dateTime(start)],
        ["Geç Start Verildiyse Sebebi", delay],
        ["Makinadan Ürün Çıkma Zamanı", dateTime(first)],
        ["Ürün Geç Çıkma Sebebi", firstDelay],
      ]}
    />
  );
}
function ReadSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="shift-read-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}
function ReadGrid({ entries }: { entries: [string, unknown][] }) {
  return (
    <dl className="shift-read-grid">
      {entries.map(([label, entry], index) => (
        <div key={`${label}-${index}`}>
          <dt>{label}</dt>
          <dd>{value(entry)}</dd>
        </div>
      ))}
    </dl>
  );
}
function ReadTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: unknown[][];
}) {
  return (
    <div className="shift-paper-scroll">
      <table className="shift-paper-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr key={index}>
                {row.map((entry, cell) => (
                  <td key={cell}>{value(entry)}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
